import {
  Avatar,
  Box,
  createStyles,
  Fade,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  WithStyles,
  withStyles,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EmailIcon from '@material-ui/icons/Email';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import React, { Fragment } from 'react';

import {
  PlexMember,
  PlexMemberInputError,
  PlexMemberInputType,
  PlexMemberRole,
} from '../../../../../graphql/gen/types';
import { TextSizesEnum } from '../../../../../theme';
import { PlexTypography } from '../../../../shared/primitives/PlexTypography';
import { plexUserRoleTargetKey } from '../plexUserRoleTargetKey';

interface Props extends WithStyles<typeof styles> {
  userRoles: PlexMember[];
  disabledKeys: Set<string>;
  // TODO: change to (__typename, id)
  saveErorrs: PlexMemberInputError[];
  onChangeRole: (index: number, role: PlexMemberRole) => void;
  onDeleteRow: (index: number) => void;
  flashKey: string | null;
  onFlashFinished: () => void;
}

const styles = createStyles({
  avatarColumn: {
    width: '12px',
  },
  actionsColumn: {
    width: '12px',
  },
  headerRow: {
    height: 48,
    alignItems: 'center',
  },
  rowTitle: {
    fontWeight: 'bold',
    color: '#464663',
  },
  roleColumn: {
    width: '200px',
  },
  rowRoot: {
    height: 56,
  },
  avatar: {
    width: 32,
    height: 32,
  },
  roleSelector: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  tableWrapper: {},
  alignRight: {},
  dBlock: {
    display: 'block',
  },
  dNone: {
    display: 'none',
  },
  errorContainer: {
    position: 'relative',
  },
  inlineError: {
    position: 'absolute',
  },
});

const ROWS_PER_PAGE = 10;

class MembersList extends React.PureComponent<Props> {
  _handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number): void => {
    console.log(page);
  };

  render(): React.ReactElement {
    const {
      userRoles,
      classes,
      onDeleteRow,
      onChangeRole,
      flashKey,
      disabledKeys,
      saveErorrs,
      onFlashFinished,
    } = this.props;

    // TODO: implement pagination

    return (
      <Fragment>
        <Box width="100%">
          <div className={classes.tableWrapper}>
            <Table size="small">
              <TableHead>
                <TableRow className={classes.headerRow}>
                  <TableCell className={classes.avatarColumn}>{/* avatar column */}</TableCell>
                  <TableCell>
                    <PlexTypography className={classes.rowTitle} text={'NAME'} size={TextSizesEnum.XSmall} />
                  </TableCell>
                  <TableCell className={classes.roleColumn}>
                    <PlexTypography className={classes.rowTitle} text={'ROLE'} size={TextSizesEnum.XSmall} />
                  </TableCell>
                  {/*<TableCell className={classNames(classes.roleColumn, classes.rowTitle)}>Role</TableCell>*/}
                  <TableCell className={classes.actionsColumn}>{/* Actions column*/}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userRoles.map(
                  (userRole: PlexMember, index: number): React.ReactElement => {
                    const key = plexUserRoleTargetKey(userRole.user);
                    const shouldFlash = flashKey === key;
                    const shouldDisable = disabledKeys.has(key);
                    const onFlashEnd = shouldFlash ? onFlashFinished : undefined;
                    const handleDeleteRole = (): void => {
                      onDeleteRow(index);
                    };
                    const handleChangeRole = (value: PlexMemberRole): void => {
                      onChangeRole(index, value);
                    };
                    const errorMessage: string | undefined = (() => {
                      const { user } = userRole;
                      switch (user.__typename) {
                        case 'PlexInvitedUser': {
                          return saveErorrs.find(
                            (saveError) => saveError.type === PlexMemberInputType.Email && saveError.id === user.email
                          )?.message;
                        }
                        case 'PlexRegisteredUser': {
                          return saveErorrs.find(
                            (saveError) => saveError.type === PlexMemberInputType.PlexUserId && saveError.id === user.id
                          )?.message;
                        }
                        default: {
                          return undefined;
                        }
                      }
                    })();

                    const baseRowProps = {
                      key,
                      classes,
                      flash: shouldFlash,
                      disabled: shouldDisable,
                      errorMessage,
                      onFlashEnd,
                      role: userRole.role,
                      onChangeRole: handleChangeRole,
                      onDeleteRow: handleDeleteRole,
                    };

                    switch (userRole.user.__typename) {
                      case 'PlexInvitedUser': {
                        return <Row {...baseRowProps} name={userRole.user.email} />;
                      }
                      case 'PlexRegisteredUser': {
                        return <Row {...baseRowProps} name={userRole.user.name} picture={userRole.user.picture} />;
                      }
                      default: {
                        throw new Error(`Invalid __typename on ${JSON.stringify(userRole.user)}`);
                      }
                    }
                  }
                )}
              </TableBody>
            </Table>
          </div>
          <div className={classes.alignRight}>
            <TablePagination
              component="div"
              count={userRoles.length}
              rowsPerPage={ROWS_PER_PAGE}
              page={0}
              backIconButtonProps={{
                'aria-label': 'Previous Page',
              }}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
              }}
              onChangePage={this._handleChangePage}
              classes={{
                select: classes.dNone,
                selectIcon: classes.dNone,
                actions: userRoles.length > 0 ? classes.dBlock : classes.dNone,
                caption: userRoles.length > 0 ? classes.dBlock : classes.dNone,
              }}
              labelRowsPerPage=""
            />
          </div>
        </Box>
      </Fragment>
    );
  }
}

interface RowProps extends WithStyles<typeof styles> {
  picture?: string;
  flash?: boolean;
  onFlashEnd?: () => void;
  disabled: boolean;
  name: string;
  errorMessage: string | undefined;
  role: PlexMemberRole;
  onChangeRole: (value: PlexMemberRole) => void;
  onDeleteRow: () => void;
}

function Row({
  classes,
  picture,
  name,
  role,
  errorMessage,
  flash,
  disabled,
  onFlashEnd,
  onChangeRole,
  onDeleteRow,
}: RowProps): React.ReactElement {
  const content = (
    <TableRow className={classes.rowRoot}>
      <TableCell>
        {/* TODO: Default to letter avatar if no picture */}
        {picture != null ? (
          <Avatar className={classes.avatar} alt={name} src={picture} />
        ) : (
          <EmailIcon className={classes.avatar} fontSize="large" />
        )}
      </TableCell>
      <TableCell className={classes.errorContainer}>
        {name}{' '}
        {errorMessage != null && (
          <Tooltip title={errorMessage} className={classes.inlineError}>
            <ErrorOutlineIcon color="error" />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <FormControl disabled={disabled}>
          <Select
            aria-label={'Role'}
            variant={'outlined'}
            value={role}
            classes={{ root: classes.roleSelector }}
            onChange={(event: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
              const { value } = event.target;
              onChangeRole(value as PlexMemberRole);
            }}
          >
            <MenuItem value={PlexMemberRole.Owner}>Owner</MenuItem>
            <MenuItem value={PlexMemberRole.Manager}>Lead</MenuItem>
            <MenuItem value={PlexMemberRole.Writer}>Team member</MenuItem>
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <IconButton disabled={disabled} edge="end" aria-label="delete" onClick={onDeleteRow}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return flash ? (
    <Fade in onEntered={onFlashEnd}>
      {content}
    </Fade>
  ) : (
    content
  );
}

const StyledMembersList = withStyles(styles)(MembersList);

export { StyledMembersList as MembersList };
